import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import API from '../../../utils/API';
import Table from '../../../components/Table/Table';
import { connect } from 'react-redux';
import constants from '../../../utils/constants';
import tableCheckbox from '../../../components/Inputs/tableCheckbox';
import useInfiniteScroll from '../../../components/common/useInfiniteScroll';
import useWebSocket from '../../../components/common/useWebSocket';
import useWebSocketDataSync from '../../../components/common/useWebSocketDataSync';

function AlertLogs(props) {
  const navigate = useNavigate();
  const websocket = useWebSocket();
  const { user } = props;
  const permissionIds = user.permissions.map((permission) => permission.id);

  const [alerts, setAlerts] = useState([]);
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [sortByCol, setSortByCol] = useState('startTime');
  const [sortByAsc, setSortByAsc] = useState(false);
  const [startIndexForTable, setStartIndexForTable] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const tableRef = useRef();
  const tableWrapperRef = useRef();

  useEffect(() => {
    if (!permissionIds.includes(constants.permissionIds.aPlusOfficerApp)) {
      navigate('/forbidden');
    }
  }, [navigate, permissionIds]);

  const dataMap = useMemo(() => {
    const map = { data: {}, ids: [], selectedIds: {}, filteredIds: [], sortedIds: [] };
    return map;
  }, []);

  // Add a function to fetch the latest alerts
  const fetchLatestAlerts = async () => {
    try {
      const response = await API.getAlerts();
      return response.data;
    } catch (error) {
      console.error('Error fetching alerts:', error);
      setError('Error fetching alerts');
      throw error;
    }
  };

  // Add a function to process updates
  const processAlertUpdates = (latestAlerts) => {
    const updatedMap = { ...dataMap };
    const newData = {};
    const changes = [];

    // Process each alert in the latest data
    latestAlerts.forEach((alert) => {
      const startTimeDisplay = alert.createdAt ? new Date(alert.createdAt).toLocaleString() : '';
      const endTimeDisplay = alert.endedAt ? new Date(alert.endedAt).toLocaleString() : '';

      const alertData = {
        id: alert.id,
        alertName: alert.templateName,
        siteName: alert.siteName,
        startTime: alert.createdAt,
        startTimeDisplay,
        endTime: alert.endedAt,
        endTimeDisplay,
        triggeredBy: alert.triggeredByName,
        clearedBy: alert.endedByName,
        checked: updatedMap.data[alert.id]?.checked || false,
      };

      // Track if this is a new or updated alert
      if (!updatedMap.data[alert.id] || 
          JSON.stringify(updatedMap.data[alert.id]) !== JSON.stringify(alertData)) {
        changes.push(alertData);
      }

      newData[alert.id] = alertData;
    });

    // Update the data map
    updatedMap.data = newData;
    updatedMap.ids = Object.keys(newData);
    updatedMap.filteredIds = [...updatedMap.ids];

    // Preserve selected IDs
    Object.keys(updatedMap.selectedIds).forEach(id => {
      if (!newData[id]) {
        delete updatedMap.selectedIds[id];
      }
    });

    // Update the dataMap reference
    Object.assign(dataMap, updatedMap);

    // Update state
    setDataCount(updatedMap.ids.length);
    setTotalDataCount(updatedMap.ids.length);
    setAlerts(Object.values(newData));
    
    return changes;
  };

  // Handle successful updates
  const handleUpdateSuccess = (changes) => {
    console.log(`Updated ${changes.length} alerts`);
    
    // Re-sort and update the visible data without changing scroll position
    sortMap(dataMap);
    setData(generateDataSlice(dataMap));
  };

  // Use the WebSocket data sync hook
  useWebSocketDataSync({
    websocket,
    messageId: 5001,
    fetchLatestData: fetchLatestAlerts,
    processUpdates: processAlertUpdates,
    onSuccess: handleUpdateSuccess,
    onError: (error) => setError('Error updating alerts'),
    dependencies: [dataMap]
  });

  // Hook for infinite scrolling
  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndexForTable,
    startIndexForTable,
    pageSize: 100,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
  });

  // Hook for sorting data when data changes or sort column is changed
  useEffect(() => {
    if (dataLoaded) {
      if (searchValue !== '') {
        tableRef.current.search(dataMap);
      } else {
        sortMap(dataMap);
        setData(generateDataSlice(dataMap));
      }
    }
  }, [dataLoaded, dataMap, generateDataSlice, searchValue, sortMap]);

  const tableColumns = useMemo(() => [
    {
      Header: tableCheckbox,
      id: 'checkbox',
      accessor: 'checked',
      Cell: tableCheckbox,
      isVisible: true,
      width: 63,
      maxWidth: 63,
    },
    {
      Header: 'ALERT_NAME',
      accessor: 'alertName',
      id: 'alertName',
      isVisible: true,
    },
    {
      Header: 'SITE_NAME',
      accessor: 'siteName',
      id: 'siteName',
      isVisible: true,
    },
    {
      Header: 'START_TIME',
      id: 'startTime',
      accessor: 'startTimeDisplay',
      isVisible: true,
    },
    {
      Header: 'END_TIME',
      id: 'endTime',
      accessor: 'endTimeDisplay',
      isVisible: true,
    },
    {
      Header: 'TRIGGERED_BY',
      accessor: 'triggeredBy',
      id: 'triggeredBy',
      isVisible: true,
    },
    {
      Header: 'CLEARED_BY',
      accessor: 'clearedBy',
      id: 'clearedBy',
      isVisible: true,
    },
  ], []);

  const handleExportCsv = () => {
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date);
    };

    const selectedAlerts = Object.values(dataMap.data).filter(alert => selectedIds[alert.id]);
    const csvData = selectedAlerts.map((alert) => ({
      'Alert Name': alert.alertName,
      'Site Name': alert.siteName,
      'Start Time': formatDate(alert.startTime),
      'End Time': alert.endTime ? formatDate(alert.endTime) : '',
      'Triggered By': alert.triggeredBy,
      'Cleared By': alert.clearedBy,
    }));

    const escapeCsvField = (field) => {
      if (typeof field === 'string') {
        return `"${field.replace(/"/g, '""')}"`;
      }
      return field;
    };

    const csv = [
      Object.keys(csvData[0]).map(escapeCsvField).join(','), // Header row
      ...csvData.map((row) => Object.values(row).map(escapeCsvField).join(',')), // Data rows
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'alert_logs.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const buttons = {
    footerButtons: (
      <>
        <button type="button" className="btn" disabled={Object.keys(selectedIds).length === 0} onClick={handleExportCsv}>
          <span><FormattedMessage id="EXPORT_AS_CSV" defaultMessage="Export as CSV" /></span>
        </button>
      </>
    ),
  }

  const tableProps = {
    pageType: constants.pageType.Log,
    modalId: null,
    columns: tableColumns,
    data, setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount, setDataCount,
    totalDataCount,
    setSortBy,
    sortByCol,
    sortByAsc,
    success, setSuccess,
    error, setError,
    startIndex: startIndexForTable,
    selectedIds, setSelectedIds,
    searchValue, 
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    buttons,
  };

  return (
    <div>
      <Table {...tableProps} />
    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(AlertLogs);
